import { useState, useCallback, useEffect } from 'react'
import { clamp } from '../lib/utils'
import type { PresentationState, PresentationControls, SlideTransition } from '../types/presentation'

export interface UsePresentationOptions {
  totalSlides: number
  initialSlide?: number
  onSlideChange?: (slideIndex: number) => void
  enableKeyboard?: boolean
}

export function usePresentation({
  totalSlides,
  initialSlide = 0,
  onSlideChange,
  enableKeyboard = true,
}: UsePresentationOptions): PresentationState & PresentationControls {
  const [currentSlide, setCurrentSlide] = useState(initialSlide)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isOverview, setIsOverview] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [transitionType, setTransitionType] = useState<SlideTransition>('slide')
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')

  // Go to specific slide
  const goToSlide = useCallback(
    (index: number) => {
      const newIndex = clamp(index, 0, totalSlides - 1)
      setDirection(newIndex > currentSlide ? 'forward' : 'backward')
      setCurrentSlide(newIndex)
      onSlideChange?.(newIndex)
    },
    [totalSlides, onSlideChange, currentSlide]
  )

  // Navigate to next slide
  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      goToSlide(currentSlide + 1)
    }
  }, [currentSlide, totalSlides, goToSlide])

  // Navigate to previous slide
  const previousSlide = useCallback(() => {
    if (currentSlide > 0) {
      goToSlide(currentSlide - 1)
    }
  }, [currentSlide, goToSlide])

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }, [])

  // Toggle overview mode
  const toggleOverview = useCallback(() => {
    setIsOverview((prev) => !prev)
  }, [])

  // Toggle pause
  const togglePause = useCallback(() => {
    setIsPaused((prev) => !prev)
  }, [])

  // Set transition type
  const setTransition = useCallback((transition: SlideTransition) => {
    setTransitionType(transition)
  }, [])

  // Keyboard navigation
  useEffect(() => {
    if (!enableKeyboard) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
        case 'PageDown':
          e.preventDefault()
          nextSlide()
          break

        case 'ArrowLeft':
        case 'ArrowUp':
        case 'PageUp':
          e.preventDefault()
          previousSlide()
          break

        case 'Home':
          e.preventDefault()
          goToSlide(0)
          break

        case 'End':
          e.preventDefault()
          goToSlide(totalSlides - 1)
          break

        case 'f':
        case 'F11':
          e.preventDefault()
          toggleFullscreen()
          break

        case 'o':
        case 'Escape':
          e.preventDefault()
          if (isOverview) {
            toggleOverview()
          }
          break

        case 'p':
          e.preventDefault()
          togglePause()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    enableKeyboard,
    nextSlide,
    previousSlide,
    goToSlide,
    totalSlides,
    toggleFullscreen,
    toggleOverview,
    togglePause,
    isOverview,
  ])

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  return {
    currentSlide,
    totalSlides,
    isFullscreen,
    isOverview,
    isPaused,
    transitionType,
    direction,
    goToSlide,
    nextSlide,
    previousSlide,
    toggleFullscreen,
    toggleOverview,
    togglePause,
    setTransition,
  }
}
